import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_URL } from "@/components/base/Seo";
import { getSpotBySlug } from "@/services/touristSpotService";
import type { TouristSpot } from "@/services/touristSpotService";
import SpotHero from "./components/SpotHero";
import SpotInfo from "./components/SpotInfo";
import SpotMap from "./components/SpotMap";
import SpotNearbySpots from "./components/SpotNearbySpots";
import SpotNearbyBusinesses from "./components/SpotNearbyBusinesses";

export default function SpotPage() {
  const { slug = "" } = useParams();
  const [spot, setSpot] = useState<TouristSpot | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getSpotBySlug(slug)
      .then((result) => {
        if (active) setSpot(result);
      })
      .catch(() => {
        if (active) setSpot(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-center px-6 py-32">
          <div className="flex flex-col items-center gap-4 text-foreground-500">
            <span className="flex h-10 w-10 animate-spin items-center justify-center rounded-full border-2 border-background-300 border-t-primary-500" />
            <p className="text-sm">Loading tourist spot...</p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  if (!spot) {
    return (
      <SiteLayout>
        <section className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-6 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-camera-lens-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            Spot not found
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            We could not find that tourist spot. Explore the districts instead to discover places to
            visit along the Canara coast.
          </p>
          <Link
            to="/locations"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            Browse locations
          </Link>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Seo
        title={`${spot.name} - ${spot.category} in ${spot.districtName}, Karnataka`}
        description={`${spot.blurb} Plan your visit to ${spot.name} in ${spot.districtName} with timings, entry fee, best time to visit and nearby businesses.`}
        keywords={`${spot.name}, ${spot.category} ${spot.districtName}, places to visit ${spot.districtName}, tourist spots coastal Karnataka, ${spot.name} timings`}
        path={`/spot/${spot.slug}`}
        image={spot.image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          name: spot.name,
          description: spot.description,
          image: spot.image,
          address: {
            "@type": "PostalAddress",
            streetAddress: spot.address,
            addressRegion: "Karnataka",
            addressCountry: "IN",
          },
          touristType: spot.category,
          url: `${SITE_URL}/spot/${spot.slug}`,
        }}
      />

      <SpotHero spot={spot} />

      <section className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_380px]">
          <div className="flex flex-col gap-8">
            <SpotInfo spot={spot} />
          </div>
          <div className="flex flex-col gap-6">
            <SpotMap spot={spot} />
            <SpotNearbySpots
              districtSlug={spot.districtSlug}
              districtName={spot.districtName}
              excludeSlug={spot.slug}
            />
          </div>
        </div>

        <SpotNearbyBusinesses
          districtSlug={spot.districtSlug}
          districtName={spot.districtName}
          spotName={spot.name}
        />
      </section>
    </SiteLayout>
  );
}